// KineticEnergy.js
import React, { useState, useCallback } from "react";
import MainCalculatorsList from "./MainCalculators";
import SharedHelmet from "../Helmet";
import RelatedLinks from "../RelatedLinks";
import { FirstAds, SecondAds,MobileUnderCalAds } from "../../Ads";
const TITLE = "Menghitung Energi Kinetik - Hitung Energi Kinetik, Massa, atau Kecepatan";
const DESCRIPTION =
  "Kalkulator energi kinetik komprehensif: Hitung energi kinetik, massa, atau kecepatan berdasarkan nilai input massa, kecepatan, atau energi kinetik. Alat yang akurat dan mudah digunakan.";
const KEYWORDS =
  "energi kinetik, menghitung energi kinetik, kalkulator energi kinetik, menghitung massa, menghitung kecepatan, fisika";
const OG_URL = "https://do-calculate.com/calculator/id/kineticenergy";
function KineticEnergyCalculator() {
  const [mass, setMass] = useState("");
  const [speed, setSpeed] = useState("");
  const [kineticEnergy, setKineticEnergy] = useState("");
  const [calculationMode, setCalculationMode] = useState(
    "calculateKineticEnergy"
  );
  const [error, setError] = useState("");
  const [showSteps, setShowSteps] = useState(false);
  const updateCalculations = useCallback(() => {
    setError("");
    const massValue = parseInt(mass, 10) || 0;
    const speedValue = parseInt(speed, 10) || 0;
    const kineticEnergyValue = parseInt(kineticEnergy, 10) || 0;
    switch (calculationMode) {
      case "calculateKineticEnergy":
        if (massValue > 0 && speedValue > 0) {
          const calculatedKineticEnergy =
            0.5 * massValue * speedValue * speedValue;
          setKineticEnergy(Math.round(calculatedKineticEnergy));
        } else {
          setError("Harap masukkan massa dan kecepatan dengan benar");
        }
        break;
      case "calculateMass":
        if (kineticEnergyValue > 0 && speedValue > 0) {
          const calculatedMass =
            (2 * kineticEnergyValue) / (speedValue * speedValue);
          setMass(Math.round(calculatedMass));
        } else {
          setError("Harap masukkan energi kinetik dan kecepatan dengan benar");
        }
        break;
      case "calculateSpeed":
        if (kineticEnergyValue > 0 && massValue > 0) {
          const calculatedSpeed = Math.sqrt(
            (2 * kineticEnergyValue) / massValue
          );
          setSpeed(Math.round(calculatedSpeed));
        } else {
          setError("Harap masukkan energi kinetik dan massa dengan benar");
        }
        break;
      default:
        break;
    }
  }, [mass, speed, kineticEnergy, calculationMode]);
  const handleCalculateClick = () => {
    updateCalculations();
    setShowSteps(true);
  };
  const handleMassChange = (e) => setMass(e.target.value);
  const handleSpeedChange = (e) => setSpeed(e.target.value);
  const handleKineticEnergyChange = (e) => setKineticEnergy(e.target.value);
  const handleModeChange = (e) => setCalculationMode(e.target.value);
  const getSolutionSteps = () => {
    switch (calculationMode) {
      case "calculateKineticEnergy":
        return (
          <div className="alert alert-info">
            <h4>Langkah-langkah Menghitung Energi Kinetik:</h4>
            <ol>
              <li>Masukkan massa (dalam kilogram) pada kolom yang disediakan.</li>
              <li>Masukkan kecepatan (dalam meter/detik) pada kolom yang disediakan.</li>
              <li>Tekan tombol "Hitung" untuk mendapatkan energi kinetik.</li>
              <li>Energi kinetik sama dengan 0.5 × massa × kecepatan².</li>
            </ol>
          </div>
        );
      case "calculateMass":
        return (
          <div className="alert alert-info">
            <h4>Langkah-langkah Menghitung Massa:</h4>
            <ol>
              <li>Masukkan energi kinetik (dalam joule) pada kolom yang disediakan.</li>
              <li>Masukkan kecepatan (dalam meter/detik) pada kolom yang disediakan.</li>
              <li>Tekan tombol "Hitung" untuk mendapatkan massa.</li>
              <li>Massa sama dengan (2 × energi kinetik) / kecepatan².</li>
            </ol>
          </div>
        );
      case "calculateSpeed":
        return (
          <div className="alert alert-info">
            <h4>Langkah-langkah Menghitung Kecepatan:</h4>
            <ol>
              <li>Masukkan energi kinetik (dalam joule) pada kolom yang disediakan.</li>
              <li>Masukkan massa (dalam kilogram) pada kolom yang disediakan.</li>
              <li>Tekan tombol "Hitung" untuk mendapatkan kecepatan.</li>
              <li>Kecepatan sama dengan √(2 × energi kinetik) / massa.</li>
            </ol>
          </div>
        );
      default:
        return null;
    }
  };
  return (
    <div>
      <SharedHelmet
        TITLE={TITLE}
        DESCRIPTION={DESCRIPTION}
        KEYWORDS={KEYWORDS}
        OG_URL={OG_URL}
      />
      <div className="row">
        <div className="col-12 col-md-8">
          <FirstAds />
        </div>
      </div>
      <div className="row">
        <div className="col-12 col-md-8">
          <br />
          <div
            className="bg-success text-white text-center"
            style={{ height: "50px" }}
          >
            <h1 style={{ fontSize: "17px" }}>{TITLE}</h1>
          </div>
          <div className="border border-light">
            <div className="row">
              <div>
                <p>
                  Kalkulator energi kinetik ini memungkinkan Anda melakukan tiga jenis perhitungan
                  berdasarkan nilai input. Pilih jenis perhitungan yang sesuai dan masukkan
                  nilai yang tersedia untuk menghitung nilai yang hilang dengan akurat dan mudah.
                </p>
                <h2 style={{ fontSize: "17px" }}>
                  Menemukan Energi Kinetik dengan Massa dan Kecepatan yang Diketahui
                </h2>
                <p>
                  Jika Anda mengetahui massa (dalam kilogram) dan kecepatan (dalam meter/detik),
                  Anda dapat menghitung energi kinetik (dalam joule) menggunakan rumus berikut:
                </p>
                <p>
                  <b>Energi Kinetik = 0.5 × Massa × Kecepatan²</b>
                </p>
                <h2 style={{ fontSize: "17px" }}>
                  Menemukan Massa dengan Energi Kinetik dan Kecepatan yang Diketahui
                </h2>
                <p>
                  Jika Anda mengetahui energi kinetik (dalam joule) dan kecepatan (dalam meter/detik),
                  Anda dapat menghitung massa (dalam kilogram) menggunakan rumus berikut:
                </p>
                <p>
                  <b>Massa = (2 × Energi Kinetik) / Kecepatan²</b>
                </p>
                <h2 style={{ fontSize: "17px" }}>
                  Menemukan Kecepatan dengan Massa dan Energi Kinetik yang Diketahui
                </h2>
                <p>
                  Jika Anda mengetahui massa (dalam kilogram) dan energi kinetik (dalam joule),
                  Anda dapat menghitung kecepatan (dalam meter/detik) menggunakan rumus berikut:
                </p>
                <p>
                  <b>Kecepatan = √((2 × Energi Kinetik) / Massa)</b>
                </p>
                <p>
                  Gunakan alat ini untuk memasukkan nilai yang tersedia dan menghitung nilai yang hilang
                  dengan akurat, memudahkan Anda melakukan perhitungan fisika terkait energi kinetik.
                </p>
                <hr />
              </div>
            </div>
            <MobileUnderCalAds />
            <div className="row">
              <div className="col-sm-12 col-md-7 calculatorStyle">
                <div className="p-3 mb-2 bg-success text-white">
                  <h2 style={{ fontSize: "16px" }}>{TITLE}</h2>
                </div>
                <hr />
                <div >
                  <div className="form-group align-items-center">
                    <b>
                      <label
                        className="form-label ms-2"
                        htmlFor="calculationMode"
                      >
                        Pilih Jenis Perhitungan
                      </label>
                    </b>
                    <div id="calculationMode">
                      <br />
                      <div>
                        <input
                          type="radio"
                          id="calculateKineticEnergy"
                          name="calculationMode"
                          value="calculateKineticEnergy"
                          checked={calculationMode === "calculateKineticEnergy"}
                          onChange={handleModeChange}
                        />
                        <label
                          htmlFor="calculateKineticEnergy"
                          className="ms-2"
                        >
                          Hitung Energi Kinetik
                        </label>
                      </div>
                      <div className="ms-3">
                        <input
                          type="radio"
                          id="calculateMass"
                          name="calculationMode"
                          value="calculateMass"
                          checked={calculationMode === "calculateMass"}
                          onChange={handleModeChange}
                        />
                        <label htmlFor="calculateMass" className="ms-2">
                          Hitung Massa
                        </label>
                      </div>
                      <div className="ms-3">
                        <input
                          type="radio"
                          id="calculateSpeed"
                          name="calculationMode"
                          value="calculateSpeed"
                          checked={calculationMode === "calculateSpeed"}
                          onChange={handleModeChange}
                        />
                        <label htmlFor="calculateSpeed" className="ms-2">
                          Hitung Kecepatan
                        </label>
                      </div>
                    </div>
                  </div>
                  <br />
                  {calculationMode !== "calculateMass" && (
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label className="form-label ms-2" htmlFor="massInput">
                          Massa (kilogram)
                        </label>
                      </b>
                      <input
                        id="massInput"
                        type="number"
                        placeholder="80"
                        value={mass}
                        onChange={handleMassChange}
                        className="form-control"
                        style={{ width: "90%" }}
                      />
                    </div>
                  )}
                  {calculationMode !== "calculateSpeed" && (
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label className="form-label ms-2" htmlFor="speedInput">
                          Kecepatan (meter/detik)
                        </label>
                      </b>
                      <input
                        id="speedInput"
                        type="number"
                        placeholder="8"
                        value={speed}
                        onChange={handleSpeedChange}
                        className="form-control"
                        style={{ width: "90%" }}
                      />
                    </div>
                  )}
                  {calculationMode !== "calculateKineticEnergy" && (
                    <div className="form-group d-flex align-items-center">
                      <b>
                        <label
                          className="form-label ms-2"
                          htmlFor="kineticEnergyInput"
                        >
                          Energi Kinetik (joule)
                        </label>
                      </b>
                      <input
                        id="kineticEnergyInput"
                        type="number"
                        placeholder="320"
                        value={kineticEnergy}
                        onChange={handleKineticEnergyChange}
                        className="form-control"
                        style={{ width: "90%" }}
                      />
                    </div>
                  )}
                  <br />
                  <button
                    className="btn btn-success"
                    style={{ fontSize: "20px" }}
                    onClick={handleCalculateClick}
                  >
                    Hitung
                  </button>
                  <br/>                  <div>
                    { (
                      <>
                        {error !== "" && (
                          <p className="p-3 mb-2 bg-warning text-white">
                            Kesalahan: {error}
                          </p>
                        )}
                        {calculationMode === "calculateKineticEnergy" &&
                          kineticEnergy !== "" && (
                            <p className="p-3 mb-2 bg-success text-white">
                              Energi Kinetik (joule): {kineticEnergy} joule
                            </p>
                          )}
                        {calculationMode === "calculateMass" && mass !== "" && (
                          <p className="p-3 mb-2 bg-success text-white">
                            Massa (kilogram): {mass} kilogram
                          </p>
                        )}
                        {calculationMode === "calculateSpeed" &&
                          speed !== "" && (
                            <p className="p-3 mb-2 bg-success text-white">
                              Kecepatan (meter/detik): {speed} meter/detik
                            </p>
                          )}
                        {showSteps && getSolutionSteps()}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <hr />
            <div className="row">
              <RelatedLinks />
              <hr />
            </div>
          </div>
        </div>
        <div className="col">
          <SecondAds />
          <br />
          <MainCalculatorsList></MainCalculatorsList>
          <br />
        </div>
         </div>
</div>
  );
}
export default KineticEnergyCalculator;